import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios'
import { format } from 'date-fns'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  TableRow,
  TableCell,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear';
import TableComponent from '../../../components/Table/TableComponent';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import ChartButton from '../../../components/Button/ChartButton';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import Chart from '../../../components/Chart'
import { BloodPressure, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const BloodPressureForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  console.log(patientId);
  
  const [bloodPressureRecords, setBloodPressureRecords] = useState<BloodPressure[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [showChart, setShowChart] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success')
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: ''
  })

  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  useEffect(() => {
    fetchBloodPressureRecords(dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage, searchTerm])

  const fetchBloodPressureRecords = useCallback(async (startDate = '', endDate = '')=> {
    setBloodPressureRecords([])
    setLoading(true)
    try {
      const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
        params: {
          limit: rowsPerPage,
          page: page + 1, // Adjust for 0-indexed page
          from: startDate || '',
          to: endDate || '',
          order_by: 'details->date',
          dir: 2,
          user_id: patientId,
          slug: 'blood-pressure',
          searchkey: searchTerm || undefined
        },
        headers: getAxiosConfig ().headers
      })

      const { vitals } = response.data.data
      console.log('Fetched vitals:', vitals)
      const total = response.data.data.pagination!.total
      console.log('total', total)
      if (Array.isArray(vitals) && vitals.length > 0) {
        const formattedData: BloodPressure[] = vitals.map((vital) => ({
          patientId: vital.patientId,
          id: vital.id,
          date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
          time: vital.details.time,
          systolic: vital.details.systolic,
          diastolic: vital.details.diastolic,
          pulse: vital.details.pulse,
          bpFlagColor: vital.details.bpFlagColor,
          range_code: vital.details.range_code,
          bpFlag: vital.details.bpFlag,
          rangeCode: vital.details.rangeCode,
          freeze: vital.freeze
        }))

        setBloodPressureRecords(formattedData)
        setTotalRecords(total)
      }
    } catch (error) {
      console.error('Error fetching blood pressure records:', error)
      showSnackbar('Failed to fetch blood pressure records. Please try again later.', 'error')
    } finally {
      setLoading(false)
    }
  }, []);

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setPage(0); // Reset to the first page
    setRowsPerPage(5); // Reset rows per page if needed
    fetchBloodPressureRecords(); // Fetch all records again
    setFiltersApplied(false); // Reset the filters applied state
  };

  const renderRow = (item: BloodPressure, index: number) => (
    <TableRow key={index}>
      <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}> {page * rowsPerPage + index + 1}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.date}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.time}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.systolic}</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.diastolic} mmHg</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.pulse} bpm</TableCell>
      <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
        <span
          style={{
            backgroundColor: item.range_code,
            padding: '4px 8px',
            borderRadius: '8px',
            color: '#F9F9F9'
          }}
        >
          {item.bpFlag}
        </span>
      </TableCell>
      
    </TableRow>
  )

  const chartData = {
    labels: bloodPressureRecords.map((record) => record.date),
    datasets: [
      {
        label: 'Systolic (mmHg)',
        data: bloodPressureRecords.map((record) => record.systolic),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        borderWidth: 2,

      },
      {
        label: 'Diastolic (mmHg)',
        data: bloodPressureRecords.map((record) => record.diastolic),
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderWidth: 2,

      },
      {
        label: 'Pulse (bpm)',
        data: bloodPressureRecords.map((record) => record.pulse),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderWidth: 2,

      }
    ]
  }
  const chartOptions = {
    responsive: true,
    animations: {
      tension: {
        easing: 'linear',
        from: 0.4, // Start tension
        to: 0.0,   // End tension
        // loop: true,
      },
    },
  };


  return (
    <div>
      <div className="flex mb-4 justify-between">
        <div>
          <div className="flex gap-4">
            <DateRangePicker
              startDateId={'BloodPressure_Start_Date'}
              endDateId={'BloodPressure_End_Date'}
              onChange={(dates: { startDate: string; endDate: string }) => {
                setDateRange(dates);
                setFiltersApplied(true);
              }}
              value={dateRange} // Pass the selected date range
            />
            {/* Added gap and centered items */}
            <IconButton
              icon={<SearchOutlinedIcon />}
              onClick={() => {
                fetchBloodPressureRecords(dateRange.startDate, dateRange.endDate);
                setFiltersApplied(true); // Mark filters as applied when search is clicked
              }}
            />

            {filtersApplied && (
              <IconButton
                icon={<ClearIcon />}
                onClick={handleClearFilters}
              />
            )}

          </div>
        </div>

        <div className="flex justify-end space-x-2"> 
          <ChartButton
            text={showChart ? 'List' : 'Chart'}
            onClick={() => {
              setShowChart(prev => !prev); // Toggle the chart view
            }}
          />
          
        </div>
      </div>
      {loading ? (
        <div className="text-center my-4">
         
        </div>
      ) : (
        <>
          {showChart ? (
            <Chart data={chartData} options={chartOptions} />
          ) : (
            <div>
              <TableComponent
                headers={[
                  'S.No',
                  'Date',
                  'Time',
                  'Systolic',
                  'Diastolic',
                  'Pulse',
                  'Result'
                ]}
                data={bloodPressureRecords}
                loading={loading}
                error={'error'}
                renderRow={renderRow}
              />

            </div>
          )}
        </>
      )}


      <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
    </div>
  )
}

export default BloodPressureForm
